import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Group,
  Modal,
  NumberInput,
  Pagination,
  SegmentedControl,
  Select,
  Switch,
  Table,
  TextInput,
} from '@mantine/core';
import type { MutableRefObject } from 'react';
import type { HouseAccountCustomerView, HouseAccountReportView } from '../../../../../services/house-accounts/house-accounts.service';
import { houseAccountsService } from '../../../../../services/house-accounts/house-accounts.service';
import type { PrinterView } from '../../../../../services/printers/printers.service';
import type { PosStreamEvent } from '../../../../../services/pos-stream/pos-stream.service';
import { Button } from '../../../../../components/Button';
import type { HouseAccountSettlementMethod } from 'shared-models/browser';
import { systemHouseAccountReceivableLabel } from 'shared-models/browser';
import { formatMoney } from '../../../../../utils/money';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from './house-accounts-modal.module.scss';
import { previousMonthStatementRange, formatStatementDate, printHouseAccountStatement } from './house-account-statement.utils';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';

type HouseAccountEvent = Extract<PosStreamEvent, { type: 'house-account.customer.upserted' }>;
type ViewMode = 'customers' | 'report';
type EditorMode = 'none' | 'customer' | 'card' | 'settlement';
type CustomerForm = {
  fullName: string;
  mobilePhone: string;
  email: string;
  notes: string;
  unlimited: boolean;
  creditLimit: number | '';
  billingDay: number;
};

const PAGE_SIZE = 20;
const EMPTY_FORM: CustomerForm = {
  fullName: '', mobilePhone: '', email: '', notes: '', unlimited: false, creditLimit: 10000, billingDay: 1,
};

const settlementLabel = (method: HouseAccountSettlementMethod | null, isSystem: boolean): string => {
  const tender = method === 'credit' ? 'באשראי' : method === 'bank_transfer' ? 'בהעברה בנקאית' : 'במזומן';
  return `${isSystem ? 'קבלת תשלום' : 'תשלום חוב'} ${tender}`;
};

export type HouseAccountsModalProps = {
  opened: boolean;
  initialView: ViewMode;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  printers: PrinterView[];
  eventCallbackRef: MutableRefObject<((event: HouseAccountEvent) => void) | null>;
};

export const HouseAccountsModal: React.FC<HouseAccountsModalProps> = ({
  opened,
  initialView,
  onClose,
  tenantId,
  employeeSession,
  printers,
  eventCallbackRef,
}) => {
  const [view, setView] = useState<ViewMode>(initialView);
  const [items, setItems] = useState<HouseAccountCustomerView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [statementRange, setStatementRange] = useState(previousMonthStatementRange);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [report, setReport] = useState<HouseAccountReportView | null>(null);
  const [editor, setEditor] = useState<EditorMode>('none');
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);
  const [baseline, setBaseline] = useState<CustomerForm | null>(null);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [settlementMethod, setSettlementMethod] = useState<HouseAccountSettlementMethod>('cash');
  const [settlementAmount, setSettlementAmount] = useState<number | ''>('');
  const [cashDrawerPrinterId, setCashDrawerPrinterId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const writeLockRef = useRef(false);

  const isReportView = view === 'report';
  const selected = items.find((item) => item.id === selectedId) ?? report?.customer ?? null;

  useEffect(() => {
    if (!opened) return;
    setView(initialView);
    setStatementRange(previousMonthStatementRange());
    setSelectedId(null);
    setReport(null);
    setEditor('none');
    setError(null);
  }, [initialView, opened]);

  useEffect(() => {
    if (!opened || !employeeSession) return;
    let cancelled = false;
    setIsLoading(true);
    void houseAccountsService.list(tenantId, employeeSession, page, PAGE_SIZE, search)
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .catch((loadError: Error) => { if (!cancelled) setError(loadError.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [employeeSession, opened, page, search, tenantId]);

  useEffect(() => {
    eventCallbackRef.current = (event) => {
      setItems((previous) => {
        const index = previous.findIndex((item) => item.id === event.customer.id);
        if (index >= 0) return previous.map((item) => item.id === event.customer.id ? event.customer : item);
        if (event.created && page === 1 && search === '') return [event.customer, ...previous].slice(0, PAGE_SIZE);
        return previous;
      });
      if (event.created) setTotal((previous) => previous + 1);
      if (!isReportView && selectedId === event.customer.id) setReport(event.report);
    };
    return () => { eventCallbackRef.current = null; };
  }, [eventCallbackRef, isReportView, page, search, selectedId]);

  const loadReport = async (customerId: number, range?: { from: string; to: string }) => {
    if (!employeeSession) return;
    setError(null);
    try {
      setReport(range
        ? await houseAccountsService.report(tenantId, customerId, employeeSession, range)
        : await houseAccountsService.report(tenantId, customerId, employeeSession));
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  };

  const selectCustomer = async (customer: HouseAccountCustomerView) => {
    setSelectedId(customer.id);
    await loadReport(customer.id, isReportView ? statementRange : undefined);
  };

  const startCreate = () => {
    setSelectedId(null);
    setReport(null);
    setForm(EMPTY_FORM);
    setBaseline(null);
    setEditor('customer');
  };

  const startEdit = () => {
    if (!selected) return;
    const next: CustomerForm = {
      fullName: selected.fullName,
      mobilePhone: selected.mobilePhone,
      email: selected.email ?? '',
      notes: selected.notes ?? '',
      unlimited: selected.creditLimit === null,
      creditLimit: selected.creditLimit ?? 10000,
      billingDay: selected.billingDay,
    };
    setForm(next);
    setBaseline(next);
    setEditor('customer');
  };

  const runWrite = async (write: () => Promise<void>) => {
    if (writeLockRef.current) return;
    writeLockRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await write();
    } catch (writeError) {
      setError((writeError as Error).message);
    } finally {
      setIsSaving(false);
      writeLockRef.current = false;
    }
  };

  const saveCustomer = async () => {
    if (!employeeSession || !form.fullName.trim() || !form.mobilePhone.trim()) return;
    await runWrite(async () => {
      const common = {
        fullName: form.fullName.trim(),
        mobilePhone: form.mobilePhone.trim(),
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
        creditLimit: form.unlimited ? null : Number(form.creditLimit),
        billingDay: form.billingDay,
      };
      if (!selectedId || !baseline) {
        await houseAccountsService.create(tenantId, common, employeeSession);
      } else {
        const payload: Record<string, unknown> = {};
        if (common.fullName !== baseline.fullName) payload.fullName = common.fullName;
        if (common.mobilePhone !== baseline.mobilePhone) payload.mobilePhone = common.mobilePhone;
        if (common.email !== (baseline.email || null)) payload.email = common.email;
        if (common.notes !== (baseline.notes || null)) payload.notes = common.notes;
        const baselineLimit = baseline.unlimited ? null : Number(baseline.creditLimit);
        if (common.creditLimit !== baselineLimit) payload.creditLimit = common.creditLimit;
        if (common.billingDay !== baseline.billingDay) payload.billingDay = common.billingDay;
        if (Object.keys(payload).length === 0) {
          setEditor('none');
          return;
        }
        await houseAccountsService.update(tenantId, selectedId, payload, employeeSession);
      }
      setEditor('none');
    });
  };

  const saveCard = async () => {
    if (!employeeSession || !selectedId || !/^\d{4}$/.test(expiry) || !/^\d{3,4}$/.test(cvv) || cardNumber.length < 12) return;
    await runWrite(async () => {
      try {
        await houseAccountsService.configureCard(tenantId, selectedId, {
          cardNumber, expDateMMYY: expiry, cvv,
        }, employeeSession);
        setEditor('none');
      } finally {
        setCardNumber('');
        setExpiry('');
        setCvv('');
      }
    });
  };

  const saveSettlement = async () => {
    if (!employeeSession || !selectedId || typeof settlementAmount !== 'number' || settlementAmount <= 0) return;
    await runWrite(async () => {
      const cashDrawers = printers.filter((printer) => printer.cashDrawer);
      const resolvedDrawerId = settlementMethod === 'cash'
        ? (cashDrawerPrinterId ? Number(cashDrawerPrinterId) : cashDrawers.length === 1 ? cashDrawers[0].id : undefined)
        : undefined;
      if (settlementMethod === 'cash' && cashDrawers.length > 1 && resolvedDrawerId == null) {
        throw new Error('יש לבחור מגירת מזומן');
      }
      await houseAccountsService.settle(tenantId, selectedId, settlementMethod, settlementAmount, employeeSession, resolvedDrawerId);
      setSettlementAmount('');
      setEditor('none');
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { if (!isSaving) onClose(); }}
      onContextMenu={(event) => event.preventDefault()}
      title={isReportView ? 'דוח חשבון בית' : 'ניהול חשבונות בית'}
      size="xl"
      centered
      classNames={{ root: navyModal.root }}
    >
      <div className={classes.body} dir="rtl">
        <SegmentedControl
          value={view}
          onChange={(value) => {
            setView(value as ViewMode);
            setEditor('none');
            setSelectedId(null);
            setReport(null);
          }}
          data={[{ value: 'customers', label: 'ניהול' }, { value: 'report', label: 'דוח חשבון' }]}
          fullWidth
        />

        <Group align="end" wrap="nowrap">
          <TextInput
            {...noSelectionTextInputProps}
            label="חיפוש"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { setPage(1); setSearch(searchDraft.trim()); }
            }}
            className={classes.search}
          />
          <Button variant="confirm" onClick={() => { setPage(1); setSearch(searchDraft.trim()); }} size="sm">חפש</Button>
          {!isReportView && <Button variant="save" onClick={startCreate} size="sm">חשבון חדש</Button>}
        </Group>

        {isReportView && (
          <Group align="end" wrap="nowrap">
            <TextInput {...noSelectionTextInputProps} type="date" label="מתאריך" value={statementRange.from} onChange={(event) => {
              const value = event.currentTarget.value;
              setStatementRange((current) => ({ ...current, from: value }));
            }} />
            <TextInput {...noSelectionTextInputProps} type="date" label="עד תאריך" value={statementRange.to} onChange={(event) => {
              const value = event.currentTarget.value;
              setStatementRange((current) => ({ ...current, to: value }));
            }} />
            <Button variant="save" onClick={() => { if (selectedId != null) void loadReport(selectedId, statementRange); }} disabled={selectedId == null || statementRange.from > statementRange.to} size="sm">הפק דוח</Button>
          </Group>
        )}

        <div className={classes.layout}>
          <div className={classes.listPane}>
            {isLoading ? <p>טוען...</p> : (
              <Table striped highlightOnHover>
                <Table.Thead><Table.Tr><Table.Th>לקוח</Table.Th><Table.Th>חוב</Table.Th><Table.Th>מצב</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {items.map((item) => (
                    <Table.Tr key={item.id} onClick={() => void selectCustomer(item)} className={classes.row} data-selected={item.id === selectedId || undefined}>
                      <Table.Td>{item.fullName}<small>{item.mobilePhone}</small></Table.Td>
                      <Table.Td>{formatMoney(item.outstandingDebt)} ₪{item.systemKey && <small>לקבלה</small>}</Table.Td>
                      <Table.Td><Badge color={item.status === 'active' ? 'green' : 'red'}>{item.status === 'active' ? 'פעיל' : 'חסום'}</Badge></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / PAGE_SIZE))} size="sm" />
          </div>

          <div className={classes.detailPane}>
            {!selected && editor === 'none' && <p className={classes.empty}>{isReportView ? 'בחר חשבון להצגת דוח.' : 'בחר חשבון להצגת פרטים.'}</p>}
            {isReportView && report && editor === 'none' && (
              <>
                <div className={classes.heading}>
                  <div><h3>דוח חשבון - {report.customer.fullName}</h3><span>{report.businessName} | {report.rangeFrom} - {report.rangeTo}</span></div>
                  <Button variant="save" onClick={() => printHouseAccountStatement(report)} size="sm">הדפס</Button>
                </div>
                <dl className={classes.summary}>
                  <div><dt>יתרת פתיחה</dt><dd>{formatMoney(report.openingBalance)} ₪</dd></div>
                  <div><dt>חיובים בתקופה</dt><dd>{formatMoney(report.chargedAmount)} ₪</dd></div>
                  <div><dt>תשלומים בתקופה</dt><dd>{formatMoney(report.cashPaidAmount + report.creditPaidAmount + report.bankTransferPaidAmount)} ₪</dd></div>
                  <div className={classes.debt}><dt>יתרת סגירה</dt><dd>{formatMoney(report.leftToPay)} ₪</dd></div>
                </dl>
                <Table striped highlightOnHover>
                  <Table.Thead><Table.Tr><Table.Th>תאריך</Table.Th><Table.Th>פעולה</Table.Th><Table.Th>חיוב</Table.Th><Table.Th>תשלום</Table.Th><Table.Th>יתרה</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {report.entries.length === 0 ? (
                      <Table.Tr><Table.Td colSpan={5}>אין תנועות בתקופה שנבחרה</Table.Td></Table.Tr>
                    ) : report.entries.map((entry) => (
                      <Table.Tr key={entry.id}>
                        <Table.Td>{formatStatementDate(entry.createdAt)}</Table.Td>
                        <Table.Td>{entry.kind === 'charge' ? `חיוב הזמנה #${entry.orderId}` : settlementLabel(entry.settlementMethod, Boolean(selected?.systemKey))}</Table.Td>
                        <Table.Td>{entry.kind === 'charge' ? `${formatMoney(entry.amount)} ₪` : ''}</Table.Td>
                        <Table.Td>{entry.kind === 'settlement' ? `${formatMoney(entry.amount)} ₪` : ''}</Table.Td>
                        <Table.Td>{formatMoney(entry.balance)} ₪</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
            {!isReportView && selected && editor === 'none' && (
              <>
                <div className={classes.heading}>
                  <div><h3>{selected.fullName}</h3><span>{selected.mobilePhone}</span></div>
                  {selected.systemKey
                    ? <Badge color="blue" data-testid="house-accounts-system-badge">חשבון מערכת</Badge>
                    : <Badge color={selected.status === 'active' ? 'green' : 'red'}>{selected.status === 'active' ? 'פעיל' : 'חסום'}</Badge>}
                </div>
                <dl className={classes.summary}>
                  <div><dt>מסגרת</dt><dd>{selected.creditLimit === null ? 'ללא הגבלה' : `${formatMoney(selected.creditLimit)} ₪`}</dd></div>
                  <div><dt>יום חיוב</dt><dd>{selected.billingDay}</dd></div>
                  <div><dt>כרטיס</dt><dd>{selected.isCardConfigured ? `${selected.cardBrand ?? ''} •••• ${selected.cardLast4}` : 'לא הוגדר'}</dd></div>
                  <div><dt>סה״כ חיובים</dt><dd>{formatMoney(report?.chargedAmount ?? 0)} ₪</dd></div>
                  <div><dt>שולם במזומן</dt><dd>{formatMoney(report?.cashPaidAmount ?? 0)} ₪</dd></div>
                  <div><dt>שולם באשראי</dt><dd>{formatMoney(report?.creditPaidAmount ?? 0)} ₪</dd></div>
                  <div><dt>שולם בהעברה בנקאית</dt><dd>{formatMoney(report?.bankTransferPaidAmount ?? 0)} ₪</dd></div>
                  <div className={classes.debt}><dt>{selected.systemKey ? systemHouseAccountReceivableLabel(selected.systemKey) : 'יתרה לתשלום'}</dt><dd>{formatMoney(report?.leftToPay ?? selected.outstandingDebt)} ₪</dd></div>
                </dl>
                <Group>
                  {!selected.systemKey && <Button variant="save" onClick={startEdit} size="sm" dataTestId="house-accounts-edit">ערוך</Button>}
                  {!selected.systemKey && <Button variant="save" onClick={() => setEditor('card')} size="sm" dataTestId="house-accounts-configure-card">הגדרת כרטיס</Button>}
                  <Button variant="confirm" onClick={() => { setSettlementMethod(selected.systemKey ? 'bank_transfer' : 'cash'); setEditor('settlement'); }} disabled={selected.outstandingDebt <= 0} size="sm" dataTestId="house-accounts-settle">{selected.systemKey ? 'קבלת תשלום' : 'תשלום חוב'}</Button>
                </Group>
              </>
            )}

            {!isReportView && editor === 'customer' && (
              <div className={classes.form}>
                <TextInput {...noSelectionTextInputProps} label="שם מלא" value={form.fullName} onChange={(event) => setForm((current) => ({ ...current, fullName: event.currentTarget.value }))} />
                <TextInput {...noSelectionTextInputProps} label="טלפון" value={form.mobilePhone} onChange={(event) => setForm((current) => ({ ...current, mobilePhone: event.currentTarget.value }))} />
                <TextInput {...noSelectionTextInputProps} label="דוא״ל" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.currentTarget.value }))} />
                <TextInput {...noSelectionTextInputProps} label="הערות" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.currentTarget.value }))} />
                <Switch checked={form.unlimited} onChange={(event) => setForm((current) => ({ ...current, unlimited: event.currentTarget.checked }))} label="ללא הגבלת אשראי" />
                {!form.unlimited && <NumberInput label="מסגרת אשראי" min={0} max={10000} decimalScale={2} value={form.creditLimit} onChange={(value) => setForm((current) => ({ ...current, creditLimit: typeof value === 'number' ? value : '' }))} />}
                <NumberInput label="יום חיוב" min={1} max={28} value={form.billingDay} onChange={(value) => setForm((current) => ({ ...current, billingDay: typeof value === 'number' ? value : 1 }))} />
                <Group><Button variant="cancel" onClick={() => setEditor('none')}>ביטול</Button><Button variant="save" onClick={saveCustomer} loading={isSaving}>שמור</Button></Group>
              </div>
            )}

            {!isReportView && editor === 'card' && selected && (
              <div className={classes.form}>
                <TextInput {...noSelectionTextInputProps} label="מספר כרטיס" value={cardNumber} onChange={(event) => setCardNumber(event.currentTarget.value.replace(/\D/g, ''))} />
                <TextInput {...noSelectionTextInputProps} label="תוקף MMYY" value={expiry} maxLength={4} onChange={(event) => setExpiry(event.currentTarget.value.replace(/\D/g, ''))} />
                <TextInput {...noSelectionTextInputProps} label="CVV" type="password" value={cvv} maxLength={4} onChange={(event) => setCvv(event.currentTarget.value.replace(/\D/g, ''))} />
                <Group><Button variant="cancel" onClick={() => setEditor('none')}>ביטול</Button><Button variant="save" onClick={saveCard} loading={isSaving}>אמת ושמור</Button></Group>
              </div>
            )}

            {!isReportView && editor === 'settlement' && selected && (
              <div className={classes.form}>
                <SegmentedControl
                  data-testid="house-accounts-settlement-method"
                  value={settlementMethod}
                  onChange={(value) => setSettlementMethod(value as HouseAccountSettlementMethod)}
                  data={[
                    { value: 'cash', label: 'מזומן' },
                    // No card on file => a credit settlement can only fail.
                    ...(selected.cardLast4 ? [{ value: 'credit', label: 'אשראי' }] : []),
                    { value: 'bank_transfer', label: 'העברה בנקאית' },
                  ]}
                />
                {settlementMethod === 'cash' && printers.filter((printer) => printer.cashDrawer).length > 1 && (
                  <Select
                    label="מגירת מזומן"
                    placeholder="בחר מגירה"
                    data={printers.filter((printer) => printer.cashDrawer).map((printer) => ({ value: String(printer.id), label: printer.name }))}
                    value={cashDrawerPrinterId}
                    onChange={setCashDrawerPrinterId}
                  />
                )}
                <NumberInput label="סכום" min={0.1} max={selected.outstandingDebt} step={0.1} decimalScale={1} value={settlementAmount} onChange={(value) => setSettlementAmount(typeof value === 'number' ? value : '')} />
                <Group><Button variant="cancel" onClick={() => setEditor('none')}>ביטול</Button><Button variant="confirm" onClick={saveSettlement} loading={isSaving}>בצע תשלום</Button></Group>
              </div>
            )}
          </div>
        </div>
        {error && <p className={classes.error}>{error}</p>}
      </div>
    </Modal>
  );
};
